import React, { useEffect, useState } from 'react';
import { ERouteLinks } from 'models/route';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useParams } from 'react-router-dom';
import { StyledPageTitle } from 'theme/styles';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { useQuery } from 'hooks/useQuery';
import { ELegalEntityTypes, ELegalEntityTypesLabel } from 'constants/LegalEntityTypes';
import { StyledDetailsWrapper } from 'pages/LegalEntitiesPage/LegalEntityDeleteCommentPage/styles';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { Box } from '@mui/system';

const LegalEntityDeletePreviousNamesSurnamesPage = () => {
  const { id, previousNamesSurnamesId } = useParams<{
    id: string;
    previousNamesSurnamesId: string;
  }>();
  const query = useQuery();
  const { t } = useTranslations();

  const {
    getLegalEntityPreviouNameSurnameData,
    handleDeletePreviousNamesSurnames,
    legalEntityPreviousNamesSurnamesDetail,
    navigateToDetails,
  } = useLegalEntities();

  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);

  const type: string = query.get('type')?.trim() || '';

  useEffect(() => {
    (async function getLEPreviousNamesSurnamesDataInit() {
      if (previousNamesSurnamesId && !legalEntityPreviousNamesSurnamesDetail) {
        getLegalEntityPreviouNameSurnameData(previousNamesSurnamesId);
      }
    })();
  }, [previousNamesSurnamesId]);

  const onDelete = async () => {
    setIsDeleteLoading(true);
    try {
      if (legalEntityPreviousNamesSurnamesDetail && previousNamesSurnamesId) {
        const result = await handleDeletePreviousNamesSurnames(previousNamesSurnamesId);

        if (result) {
          navigateToDetails();
        }
      }
    } catch (e: any) {
      console.error(e);
    }
    setIsDeleteLoading(false);
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.legalEntity.name'), route: ERouteLinks.LegalEntities },
          {
            label:
              type === ELegalEntityTypes.individual
                ? t('legalEntities.individual.accordion.label')
                : t('legalEntities.organization.accordion.label'),
            route: `${ERouteLinks.LegalEntities}?expand=${
              type === ELegalEntityTypes.individual
                ? ELegalEntityTypesLabel.individual
                : ELegalEntityTypesLabel.organisation
            }`,
          },
          {
            label: t('legalEntityDetails.legalEntityDetails.text'),
            route: `${ERouteLinks.ViewLegalEntity.replace(':id', id as string)}?type=${
              type === ELegalEntityTypes.individual
                ? ELegalEntityTypesLabel.individual
                : ELegalEntityTypesLabel.organisation
            }&expand=previousNamesSurnames`,
          },
          t('legalEntityDetails.previousNamesSurnames.accordion.label'),
          t('legalEntityDetails.previousNamesSurnames.delete.header'),
        ]}
      />
      {legalEntityPreviousNamesSurnamesDetail && (
        <>
          <StyledPageTitle variant="h4">
            {t('legalEntityDetails.previousNamesSurnames.delete.header')}
          </StyledPageTitle>

          <Box sx={{ mb: 3 }}>
            <Alert
              text={t('legalEntityDetails.previousNamesSurnames.areYouSureRemove.text')}
              variant={EAlertVariants.warning}
            />
          </Box>

          <StyledDetailsWrapper container sx={{ rowGap: 3 }}>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.previousNamesSurnamesTab.firstName.column')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(legalEntityPreviousNamesSurnamesDetail.firstName)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.previousNamesSurnamesTab.middleName.column')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(legalEntityPreviousNamesSurnamesDetail.middleNames)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntityDetails.previousNamesSurnamesTab.surname.column')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(legalEntityPreviousNamesSurnamesDetail.lastName)}
              </Typography>
            </Grid>
          </StyledDetailsWrapper>
          <Grid container sx={{ mt: 2 }} spacing={2}>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Button
                fullWidth
                label={t('general.yes.button')}
                id="yes-button"
                variant={EButtonVariants.contained}
                sx={{ mr: 1 }}
                onClick={async () => onDelete()}
                isLoading={isDeleteLoading}
              />
            </Grid>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Button
                fullWidth
                label={t('general.no.button')}
                id="no-button"
                variant={EButtonVariants.outlined}
                onClick={navigateToDetails}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export { LegalEntityDeletePreviousNamesSurnamesPage };
